"use client";

import Link from "next/link";
import { useActionState } from "react";
import { registerAction, type AuthActionState } from "@/app/actions/auth";
import {
  btnPrimaryCls,
  errorTextCls,
  fieldHintCls,
  inputCls,
  labelCls,
  linkAccentCls,
} from "@/components/form-classes";
import { RSVP_LABELS } from "@/lib/rsvp-labels";

const initial: AuthActionState = null;

export function RegisterForm() {
  const [state, formAction, pending] = useActionState(registerAction, initial);

  return (
    <form action={formAction} className="mx-auto max-w-md space-y-5">
      {state?.error ? (
        <p className={errorTextCls} role="alert">
          {state.error}
        </p>
      ) : null}

      <div>
        <label htmlFor="email" className={labelCls}>
          E-Mail
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          className={inputCls}
        />
        {state?.fieldErrors?.email ? (
          <p className={`mt-1 ${errorTextCls}`}>{state.fieldErrors.email}</p>
        ) : null}
      </div>

      <div>
        <label htmlFor="password" className={labelCls}>
          Passwort
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          className={inputCls}
        />
        <p className={fieldHintCls}>Mindestens 10 Zeichen.</p>
        {state?.fieldErrors?.password ? (
          <p className={`mt-1 ${errorTextCls}`}>{state.fieldErrors.password}</p>
        ) : null}
      </div>

      <div>
        <label htmlFor="displayName" className={labelCls}>
          Anzeigename
        </label>
        <input
          id="displayName"
          name="displayName"
          type="text"
          autoComplete="name"
          required
          maxLength={120}
          className={inputCls}
        />
        {state?.fieldErrors?.displayName ? (
          <p className={`mt-1 ${errorTextCls}`}>
            {state.fieldErrors.displayName}
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor="rsvp" className={labelCls}>
          Rückmeldung
        </label>
        <select
          id="rsvp"
          name="rsvp"
          required
          defaultValue=""
          className={inputCls}
        >
          <option value="" disabled>
            Bitte wählen …
          </option>
          {(Object.keys(RSVP_LABELS) as (keyof typeof RSVP_LABELS)[]).map(
            (key) => (
              <option key={key} value={key}>
                {RSVP_LABELS[key]}
              </option>
            ),
          )}
        </select>
        {state?.fieldErrors?.rsvp ? (
          <p className={`mt-1 ${errorTextCls}`}>{state.fieldErrors.rsvp}</p>
        ) : null}
      </div>

      <div>
        <label htmlFor="notes" className={labelCls}>
          Notizen{" "}
          <span className="font-normal text-zinc-500">(optional)</span>
        </label>
        <textarea
          id="notes"
          name="notes"
          rows={3}
          maxLength={2000}
          className={inputCls}
          placeholder="Allergien, +1, Ankunftszeit …"
        />
        {state?.fieldErrors?.notes ? (
          <p className={`mt-1 ${errorTextCls}`}>{state.fieldErrors.notes}</p>
        ) : null}
      </div>

      <div>
        <label htmlFor="phone" className={labelCls}>
          Telefon{" "}
          <span className="font-normal text-zinc-500">
            (optional, für SMS/WhatsApp)
          </span>
        </label>
        <input
          id="phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          className={inputCls}
          placeholder="+43 … oder 0…"
        />
        <p className={fieldHintCls}>
          Ich nutze die Nummer nur, falls ich dich später per SMS oder WhatsApp
          erreichen möchte — nur mit deiner Zustimmung, soweit das geplant ist.
        </p>
        {state?.fieldErrors?.phone ? (
          <p className={`mt-1 ${errorTextCls}`}>{state.fieldErrors.phone}</p>
        ) : null}
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] backdrop-blur-md">
        <label className="flex cursor-pointer gap-3 text-sm leading-snug text-zinc-200">
          <input
            type="checkbox"
            name="privacyAccepted"
            value="true"
            required
            className="mt-1 h-4 w-4 shrink-0 rounded border-white/25 bg-white/5 text-cyan-500 focus:ring-cyan-500/40"
          />
          <span>
            Ich habe die{" "}
            <Link
              href="/datenschutz"
              className={`font-semibold underline underline-offset-2 ${linkAccentCls}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Datenschutzerklärung
            </Link>{" "}
            zur Kenntnis genommen und willige in die dort beschriebene
            Verarbeitung meiner Angaben ein.
          </span>
        </label>
      </div>

      <button type="submit" disabled={pending} className={`${btnPrimaryCls} w-full`}>
        {pending ? "Wird gesendet …" : "Registrieren"}
      </button>
    </form>
  );
}
